import classnames from 'classnames'
import React from 'react'

/**
 * The company meta row's icons as Font Awesome **solid** glyphs (ENG-1366). The outlined Material
 * icons they replace read too light against the company name they sit under.
 *
 * Solid is the header's *primary* weight, so only that top row uses these: the address / phone /
 * date items of {@link QuoteDocumentHeaderDesktop}, and the same three in the mobile sheet's
 * document section. Everything below — the Prepared for / Job Address / Prepared by columns, the
 * phone compact rows, the sheet's detail rows, and the contact menus — is secondary and stays on
 * the outlined Material icons.
 *
 * Each one takes the same `{ className }` contract as a Material `SvgIcon`, so the header's
 * `icon={SomeIcon}` props and `<SomeIcon className={classes.metaIcon} />` call sites are unchanged.
 * Size and colour therefore still come from the header's own classes (see
 * `quoteDocumentHeaderStyles`), which set 13-14px in Brand P50 (#55647c).
 *
 * The kit is a global script in every app that renders this header — `apps/app/index.html`,
 * `apps/customer-view/index.html` and `apps/admin/index.html` — so these render in the editor, in the
 * customer view, and in the PDF printed from the customer view. Font Awesome is already used in the
 * customer-facing document elsewhere (see `PaymentStatusBadge`), so this adds no new dependency.
 *
 * Solid only: the kit runs in webfont (`css`) mode and serves the 900 face alone, so a `fa-regular`
 * class silently renders solid (see `packages/@paintscout/ui/src/icons/SquarePlusIcon.tsx`). An
 * outlined tier therefore has to come from Material, not from Font Awesome.
 */
function faIcon(glyph: string, displayName: string) {
  const Icon = ({ className }: { className?: string }) => (
    <i className={classnames('fa-solid', glyph, className)} aria-hidden="true" />
  )
  Icon.displayName = displayName
  return Icon
}

export const LocationIcon = faIcon('fa-location-dot', 'LocationIcon')
export const PhoneIcon = faIcon('fa-phone', 'PhoneIcon')
export const EventIcon = faIcon('fa-calendar', 'EventIcon')

/**
 * A filled version of the `materials` paint-roller in the app's `QuoteNextBreakdownIcon`. It is the
 * same path without the roller and grip cutouts, so the glyph weight matches. It sizes with `1em`
 * and inherits `color`, like a Material `SvgIcon`.
 */
export const PaintRollerIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.2 1.6C2.3175 1.6 1.6 2.3175 1.6 3.2V4.8C1.6 5.6825 2.3175 6.4 3.2 6.4H10.4C11.2825 6.4 12 5.6825 12 4.8V4.6H12.4C12.7325 4.6 13 4.8675 13 5.2V7.6C13 7.9325 12.7325 8.2 12.4 8.2H8.4C7.405 8.2 6.6 9.005 6.6 10V10.0175C6.0325 10.1125 5.6 10.6075 5.6 11.2V14C5.6 14.6625 6.1375 15.2 6.8 15.2H7.6C8.2625 15.2 8.8 14.6625 8.8 14V11.2C8.8 10.605 8.3675 10.1125 7.8 10.0175V10C7.8 9.6675 8.0675 9.4 8.4 9.4H12.4C13.395 9.4 14.2 8.595 14.2 7.6V5.2C14.2 4.205 13.395 3.4 12.4 3.4H12V3.2C12 2.3175 11.2825 1.6 10.4 1.6H3.2Z"
      fill="currentColor"
    />
  </svg>
)
PaintRollerIcon.displayName = 'PaintRollerIcon'
